import {
  FeatureSchema,
  VehicleRequirement,
  featureGet,
} from "../../../api/vehicleFeatures";
import { OrderRequirementStep } from "../../../components/VehicleFeatures/OrderRequirementStep";
/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC } from "react";
import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  OrderSummary,
  ValidationToast,
  Button,
  OrderPreview,
  OrderTemplateSaveDialog,
} from "@components";
import {
  CreateOrderStepsEnum,
  CustomerSelection,
  DriverSelection,
  ProductManagement,
  RouteSelection,
} from "./";

import {
  Customer,
  Driver,
  Order,
  OrderProduct,
  OrderTemplate,
} from "../../../models";
import { updateOrderDraft } from "../../../api/client";
import { useCache } from "../../../context/CacheContext";
import { Timestamp } from "../../../types";
import {
  IContactPerson,
  IRouteItem,
  IOrderProduct,
  IOrder,
} from "../../../types";
import { IRecurrenceRule } from "../../../types/order/IOrderTemplate";
import { useUserSession } from "../../../context/UserContext";
import { HiOutlineArrowLeft, HiOutlineArrowRight } from "react-icons/hi";
import { HiCheck } from "react-icons/hi";
import { useWorkspace } from "../../../context/WorkspaceContext";
import { useDataCache, useWorkspaceData } from "@data";
import { RouteSelectionRef } from "./steps/RouteSelection";
import { ProductSelectionRef } from "./steps/ProductManagement";
import { useNavigate } from "react-router-dom";
import { cn, layoutPage } from "@design-system";

// Props for both create and edit flows
interface CreateOrderProps {
  initialOrder?: Order;
  /**
   * When true the wizard operates in edit-mode and will persist the changes to the existing
   * order instead of creating a new one.
   */
  isEdit?: boolean;
  /** Callback executed after the order has been updated or created */
  onCompleted?: (order: Order) => void;
  /**
   * Provide a custom list of steps (in order) that should be displayed in the wizard. When
   * omitted the full default step list will be used. Use this to exclude steps like customer
   * or driver when editing an incoming public order.
   */
  editableSteps?: CreateOrderStepsEnum[];
  /**
   * When "template" the wizard authors an order template instead of an order:
   * date pickers are hidden, the driver is optional, and the final action saves
   * to the `order_templates` collection.
   */
  mode?: "order" | "template";
  /** Existing template to edit (template mode only). */
  initialTemplate?: OrderTemplate;
  /** Called after a template has been created/updated (template mode only). */
  onTemplateSaved?: () => void;
}

const CreateOrder: FC<CreateOrderProps> = function ({
  initialOrder,
  isEdit = false,
  onCompleted,
  editableSteps,
  mode = "order",
  initialTemplate,
  onTemplateSaved,
}) {
  const { t } = useTranslation(["orders", "validation"]);
  const isTemplateMode = mode === "template";
  const defaultSteps = [
    CreateOrderStepsEnum.CUSTOMER_SELECTION,
    CreateOrderStepsEnum.ROUTE_SELECTION,
    CreateOrderStepsEnum.PRODUCT_MANAGEMENT,
    CreateOrderStepsEnum.DRIVER_SELECTION,
  ];

  const steps =
    editableSteps && editableSteps.length > 0 ? editableSteps : defaultSteps;

  // Ensure we always have a valid starting step
  const [currentStep, setStep] = useState<CreateOrderStepsEnum>(steps[0]);
  // Start with provided order/template when editing, otherwise a new draft order
  const [order, setOrder] = useState<Order>(() => {
    if (isTemplateMode && initialTemplate) {
      return OrderTemplate.toOrder(initialTemplate);
    }
    return initialOrder ? (initialOrder as Order).clone() : Order.default();
  });
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [stepValidatedFailed, setStepValidatonFailed] = useState(false);
  const [transition, setTransition] = useState("");
  const { activeWorkspace } = useWorkspace();
  const { userSession } = useUserSession();
  const { invalidateOrders } = useDataCache();
  const { updateCacheKey } = useCache();
  const { workspaceSettings, locations } = useWorkspaceData();
  const navigate = useNavigate();
  const [featureSchema, setFeatureSchema] = useState<FeatureSchema>();
  const [vehicleRequirements, setVehicleRequirements] = useState<
    VehicleRequirement[]
  >([]);
  const requirementFlow = !isEdit && !isTemplateMode;
  const [featureError, setFeatureError] = useState(false);
  const [featureReload, setFeatureReload] = useState(0);
  useEffect(() => {
    setVehicleRequirements([]);
  }, [activeWorkspace?.workspaceId]);
  useEffect(() => {
    let current = true;
    setFeatureSchema(undefined);
    setFeatureError(false);
    if (requirementFlow && activeWorkspace?.workspaceId)
      featureGet<FeatureSchema>(activeWorkspace.workspaceId, "schema")
        .then((s) => {
          if (current) setFeatureSchema(s);
        })
        .catch(() => {
          if (current) setFeatureError(true);
        });
    return () => {
      current = false;
    };
  }, [activeWorkspace?.workspaceId, requirementFlow, featureReload]);

  // Seed the workspace default pickup location as the first stop for brand-new
  // orders. Runs once; the user can still edit it and add other pickup stops.
  // The location is resolved live from the workspace locations (settings store
  // only a reference id), so it always reflects the current location details.
  const didSeedDefaultPickupRef = useRef(false);
  useEffect(() => {
    if (didSeedDefaultPickupRef.current) return;
    if (isEdit || isTemplateMode || initialOrder || initialTemplate) return;

    const defaultPickupId = workspaceSettings?.defaultPickupLocationId;
    if (!defaultPickupId) return;
    if (order.route.length > 0) return;

    const defaultPickup = locations.find(
      (location) => location.id === defaultPickupId,
    );
    if (!defaultPickup) return;

    const pickupLocation =
      typeof (defaultPickup as any).data === "function"
        ? (defaultPickup as any).data()
        : { ...defaultPickup };

    const pickupRouteItem: IRouteItem = {
      location: pickupLocation,
      stopNumber: 0,
      isPickup: true,
      stopDate: Timestamp.now(),
      timing: { earliest: true, latest: false },
    };

    didSeedDefaultPickupRef.current = true;
    set("route", [pickupRouteItem]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    workspaceSettings,
    locations,
    isEdit,
    isTemplateMode,
    initialOrder,
    initialTemplate,
  ]);

  // Legg til state for ValidationToast
  const [showValidationToast, setShowValidationToast] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");

  const fadeInTransition =
    "animate-in slide-in-from-right fade-out duration-300";
  const fadeOutTransition =
    "animate-in slide-in-from-left fade-out duration-300";
  // Legg til CSS for responsiv layout - Behold desktop-utseende
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.id = "navigation-buttons-style";
    styleElement.innerHTML = `
      /* Desktop styling - samme som før */
      @media (min-width: 768px) {
        .navigation-buttons-desktop {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 1rem;
        }
        .navigation-buttons-mobile {
          display: none;
        }
      }
      
      /* Mobil styling - forbedret layout */
      @media (max-width: 767px) {
        .navigation-buttons-desktop {
          display: none;
        }
        .navigation-buttons-mobile {
          display: flex;
          justify-content: space-between;
          margin-top: 0.5rem;
          margin-bottom: 1rem;
        }
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      const styleElement = document.getElementById("navigation-buttons-style");
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  const set = (
    key: string,
    val:
      | string
      | number
      | Customer
      | IRouteItem[]
      | IOrderProduct[]
      | IContactPerson[]
      | Driver
      | Timestamp
      | Record<string, any>
      | null,
  ) => {
    setOrder((prev) => {
      const newObj =
        prev instanceof Order ? prev.clone() : new Order(prev as IOrder);
      // @ts-ignore
      newObj[key] = val;
      return newObj as Order;
    });
  };

  const handleStepChange = (step: number) => {
    // Hvis vi går bakover, tillat uten validering
    if (step < currentStep) {
      setStepValidatonFailed(false);
      setShowValidationToast(false); // Skjul toast hvis vi går bakover
      setTransition(fadeOutTransition);
      setStep(step);
      return;
    }

    // Først oppdaterer vi dataene basert på gjeldende steg
    if (currentStep === CreateOrderStepsEnum.ROUTE_SELECTION) {
      handleRouteUpdate();
    }
    if (currentStep === CreateOrderStepsEnum.PRODUCT_MANAGEMENT) {
      // For produktsteget, bruk returverdien fra handleProductUpdate
      const isValid = handleProductUpdate();
      if (!isValid) {
        // Hvis valideringen feiler, ikke gå videre
        return;
      }
    }

    // Hvis vi går til siste steg (sjåførvalg), tillat uten validering
    if (step === CreateOrderStepsEnum.DRIVER_SELECTION) {
      setStepValidatonFailed(false);
      setTransition(fadeInTransition);
      setStep(step);
      return;
    }

    // For andre steg, valider før vi går videre
    setTimeout(() => {
      setOrder((order) => {
        const validated = Order.validateStep(order, currentStep);
        if (validated) {
          setStepValidatonFailed(false);
          setShowValidationToast(false); // Skjul toast hvis validering er OK
          setTransition(fadeInTransition);
          setStep(step);
        } else {
          setStepValidatonFailed(true);

          // Vis ValidationToast med passende feilmelding basert på gjeldende steg
          let errorMessage = "";
          switch (currentStep) {
            case CreateOrderStepsEnum.CUSTOMER_SELECTION:
              errorMessage = t("validation:customer_required");
              break;
            case CreateOrderStepsEnum.ROUTE_SELECTION:
              errorMessage = t("validation:route_required");
              break;
            case CreateOrderStepsEnum.PRODUCT_MANAGEMENT:
              errorMessage = t("validation:products_required");
              break;
            default:
              errorMessage = t("validation:step_incomplete");
          }

          setValidationMessage(errorMessage);
          setShowValidationToast(true);
        }
        return order;
      });
    }, 100);
  };

  const routeSelectionRef = useRef<RouteSelectionRef>(null);
  const productSelectionRef = useRef<ProductSelectionRef>(null);

  const handleRouteUpdate = () => {
    if (routeSelectionRef.current) {
      routeSelectionRef.current.updateOrderRoute();
    }
  };

  const handleProductUpdate = (): boolean => {
    if (productSelectionRef.current) {
      // Kall updateOrderProducts og bruk returverdien for å sette stepValidatedFailed
      const isValid = productSelectionRef.current.updateOrderProducts();
      if (!isValid) {
        setStepValidatonFailed(true);

        // Sjekk om det faktisk mangler produkter
        if (order.cargo.length === 0) {
          // Vis feilmelding om manglende produkter
          setValidationMessage(t("validation:products_required"));
          setShowValidationToast(true);
        }

        return false;
      }
      return true;
    }
    return false; // Hvis ref ikke er tilgjengelig, returner false for å være på den sikre siden
  };

  const handleCancel = () => {
    // Navigerer tilbake til riktig liste avhengig av modus
    navigate(isTemplateMode ? "/order-templates" : "/orders");
  };

  // Prefill the whole wizard from a selected template ("Start with template").
  const applyTemplate = (template: OrderTemplate) => {
    setOrder(OrderTemplate.toOrder(template));
    setVehicleRequirements([]);
    setStepValidatonFailed(false);
    setShowValidationToast(false);
    setStep(steps[0]);
  };

  // Persist the current wizard state as an order template (template mode).
  const handleSaveTemplate = async (
    name: string,
    description: string,
    recurrence: IRecurrenceRule | null,
    projectNumber: string,
  ) => {
    if (!activeWorkspace) return;
    setSavingTemplate(true);
    try {
      const workspaceId = activeWorkspace.workspaceId;
      const templateData = OrderTemplate.fromOrder(
        { ...order, workspaceId } as IOrder,
        name,
        description,
      );
      templateData.projectNumber = projectNumber;
      templateData.recurrence = recurrence ?? undefined;

      if (initialTemplate?.id) {
        await OrderTemplate.update(initialTemplate, {
          name: templateData.name,
          description: templateData.description,
          customer: templateData.customer,
          customerId: templateData.customerId,
          route: templateData.route,
          cargo: templateData.cargo,
          driverId: templateData.driverId,
          comment: templateData.comment,
          projectNumber: templateData.projectNumber,
          orderPricing: templateData.orderPricing,
          stopDayOffsets: templateData.stopDayOffsets,
          recurrence: recurrence ?? undefined,
        });
      } else {
        const created = await OrderTemplate.create(workspaceId, templateData);
        if (!created) {
          throw new Error("Failed to create order template");
        }
      }

      updateCacheKey();
      setShowSaveTemplateDialog(false);
      if (onTemplateSaved) {
        onTemplateSaved();
      } else {
        navigate("/order-templates");
      }
    } catch (error) {
      console.error("Failed to save order template", error);
      setValidationMessage(t("orders:templates.toast.save_error"));
      setShowValidationToast(true);
    } finally {
      setSavingTemplate(false);
    }
  };

  // Persist updates to an existing order
  const handleSaveOrder = async () => {
    try {
      if (!activeWorkspace || !userSession) {
        throw new Error("Missing workspace or user session");
      }

      // Build payload formatted per API expectations
      const routePayload = order.route.map((r) => ({
        location: r.location,
        stopNumber: r.stopNumber,
        stopDate: r.stopDate
          ? Timestamp.toFormattedDate(r.stopDate, "YYYY-MM-DD")
          : null,
        contact: r.contact ?? null,
        timing: r.timing ?? null,
        requiredTimeWindows: r.requiredTimeWindows ?? null,
        preferredTimeWindows: r.preferredTimeWindows ?? null,
        status: (r as any).status ?? "New",
        completedAt: null,
      }));

      const cargoPayload = order.cargo.map((c) => ({
        productId: c.productId ?? null,
        descriptionShort: c.descriptionShort ?? "",
        description: c.description ?? "",
        imgUrls: c.imgUrls ?? [],
        weight: c.weight ?? 0,
        length: c.length ?? 0,
        height: c.height ?? 0,
        width: c.width ?? 0,
        price: c.price ?? 0,
        productType: c.productType ?? [],
        pricing: c.pricing ?? {
          priceModel: [],
          pricePerCargoUnit: 0,
          pricePerDistanceUnit: 0,
          pricePerVolumeUnit: 0,
          pricePerWeightUnit: 0,
          vat: 25,
        },
        quantity: c.quantity ?? 1,
        itinerary: c.itinerary,
      }));

      const payload: any = {
        ...order,
        route: routePayload,
        cargo: cargoPayload,
      };

      await updateOrderDraft(
        userSession.token.accessToken,
        activeWorkspace.workspaceId,
        order.id,
        payload,
      );

      invalidateOrders();
      if (onCompleted) {
        onCompleted(order);
      } else {
        navigate("/incoming-orders");
      }
    } catch (error: any) {
      // Handle address validation errors specifically
      if (
        error?.response?.data?.data.error?.type === "AddressValidationError"
      ) {
        const errorData = error.response.data.data.error;
        // Show validation toast with specific stop number and address
        setValidationMessage(
          t("orders:address_not_found", {
            stopNumber: errorData.stop_number,
            address: errorData.address || "Unknown address",
          }),
        );
        setShowValidationToast(true);
      } else {
        // Handle other errors generically
        setValidationMessage(t("validation:step_incomplete"));
        setShowValidationToast(true);
      }
    }
  };

  const SummaryAndCompleteDialog = (
    <Dialog
      title={t("create.summary")}
      dismissible
      size="7xl"
      content={
        <OrderSummary
          vehicleDefinitions={
            requirementFlow ? featureSchema?.definitions : undefined
          }
          order={
            requirementFlow
              ? {
                  ...order,
                  driverId: undefined,
                  // Orders without requirements retain the ordinary creation path.
                  ...(vehicleRequirements.length && featureSchema
                    ? {
                        vehicleRequirements,
                        vehicleFeatureSchemaRevision: featureSchema.revision,
                      }
                    : {}),
                }
              : order
          }
          close={() => setShowSummaryDialog(false)}
        />
      }
      show={showSummaryDialog}
      toggleModal={() => setShowSummaryDialog(false)}
      hideButtons
    />
  );

  const CancelDialog = (
    <Dialog
      title={t("create.cancel_title") || "Avbryt opprettelse av ordre"}
      dismissible
      content={
        <div>
          <p>
            {t("create.cancel_description") ||
              "Er du sikker på at du vil avbryte opprettelsen av denne ordren? Alle endringer vil gå tapt."}
          </p>
        </div>
      }
      show={showCancelDialog}
      toggleModal={() => setShowCancelDialog(false)}
      confirmButton={() => handleCancel()}
      buttonConfirmText={t("create.cancel_confirm") || "Ja, avbryt"}
      buttonCloseText={t("create.cancel_cancel") || "Nei, fortsett"}
      confirmButtonColor="failure"
    />
  );

  // Helper to find previous / next steps in the configured sequence
  const currentIdx = steps.indexOf(currentStep);
  const prevStep = currentIdx > 0 ? steps[currentIdx - 1] : null;
  const nextStep = currentIdx < steps.length - 1 ? steps[currentIdx + 1] : null;
  const isLastStep = currentIdx === steps.length - 1;

  // Navigasjonsknapper
  const NavigationButtons = (
    <div className="navigation-buttons-desktop">
      {prevStep !== null && (
        <Button
          variant="secondary"
          className="mr-2 md:text-base text-sm"
          onClick={() => handleStepChange(prevStep)}
          icon={<HiOutlineArrowLeft />}
        >
          {t("orders:create.back_button")}
        </Button>
      )}
      {nextStep !== null && (
        <Button
          variant="primary"
          onClick={() => handleStepChange(nextStep)}
          icon={<HiOutlineArrowRight />}
          iconPosition="right"
          className="bg-brand hover:bg-lgb-blue-400 text-white md:text-base text-sm"
        >
          {t("orders:create.proceed_button")}
        </Button>
      )}
      {isTemplateMode && isLastStep && (
        <Button
          variant="primary"
          onClick={() => setShowSaveTemplateDialog(true)}
          icon={<HiCheck />}
          iconPosition="right"
          className="bg-brand hover:bg-lgb-blue-400 text-white md:text-base text-sm"
        >
          {t("templates.save_template_button")}
        </Button>
      )}
      {!isTemplateMode && !isEdit && isLastStep && (
        <Button
          variant="primary"
          onClick={() => {
            if (
              requirementFlow &&
              vehicleRequirements.some((r) => r.value === "")
            ) {
              setValidationMessage(t("workspace:features.missingValue"));
              setShowValidationToast(true);
              return;
            }
            setShowSummaryDialog(true);
          }}
          icon={<HiCheck />}
          iconPosition="right"
          className="bg-brand hover:bg-lgb-blue-400 text-white md:text-base text-sm"
        >
          {t("create.summary_and_complete_button")}
        </Button>
      )}
      {!isTemplateMode && isEdit && isLastStep && (
        <Button
          variant="primary"
          disabled={false}
          onClick={handleSaveOrder}
          icon={<HiCheck />}
          iconPosition="right"
          className="bg-brand hover:bg-lgb-blue-400 text-white md:text-base text-sm"
        >
          {t("common:save")}
        </Button>
      )}
    </div>
  );

  // Render det aktuelle steget basert på currentStep
  const renderCurrentStep = () => {
    switch (currentStep) {
      case CreateOrderStepsEnum.CUSTOMER_SELECTION:
        return (
          <CustomerSelection
            selectedCustomer={order.customer as Customer}
            update={(c) => {
              set("customer", c);
              set("customerId", c.id);
            }}
            validationFailed={stepValidatedFailed}
            allowTemplateStart={!isTemplateMode && !isEdit}
            onApplyTemplate={applyTemplate}
          />
        );
      case CreateOrderStepsEnum.ROUTE_SELECTION:
        return (
          <RouteSelection
            expandStopsByDefault={false}
            hideDefaultPickupLocationId={
              !isEdit &&
              !isTemplateMode &&
              workspaceSettings?.hideDefaultPickupInOrderForm
                ? workspaceSettings?.defaultPickupLocationId
                : undefined
            }
            ref={routeSelectionRef}
            route={[...order.route]}
            update={(r) => {
              set("route", r);
            }}
            validationFailed={stepValidatedFailed}
            hideDates={isTemplateMode}
          />
        );
      case CreateOrderStepsEnum.PRODUCT_MANAGEMENT:
        return (
          <ProductManagement
            ref={productSelectionRef}
            route={order.route}
            orderItems={order.cargo as OrderProduct[]}
            orderPricing={order.orderPricing}
            validationFailed={stepValidatedFailed}
            update={(p: IOrderProduct[]) => {
              set("cargo", p);
            }}
            updateOrderPricing={(pricing) => {
              // Update the top-level orderPricing object
              const updatedOrderPricing = {
                ...(order.orderPricing || {}),
                priceModel: pricing.priceModel,
                pricePerDistanceUnit: pricing.pricePerDistanceUnit,
                pricePerTimeUnit: pricing.pricePerTimeUnit,
                timePeriodInMinutes: pricing.timePeriodInMinutes,
                vat: pricing.vat,
              };
              set("orderPricing", updatedOrderPricing);
            }}
          />
        );
      case CreateOrderStepsEnum.DRIVER_SELECTION:
        if (requirementFlow)
          return (
            <OrderRequirementStep
              schema={featureSchema}
              error={featureError}
              reload={() => setFeatureReload((v) => v + 1)}
              value={vehicleRequirements}
              onChange={setVehicleRequirements}
            />
          );
        return (
          <DriverSelection
            allowUnassigned={!isEdit}
            order={order}
            emitOnClick
            update={(id) => set("driverId", id)}
            comment={order.comment}
            validationFailed={stepValidatedFailed}
            updateComment={(c) => set("comment", c)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative">
      {/* ValidationToast for å vise feilmeldinger */}
      <ValidationToast
        show={showValidationToast}
        title={t("validation:validation_error")}
        message={validationMessage}
        onClose={() => setShowValidationToast(false)}
        position="top-right"
      />

      {/* Responsiv layout - behold desktop, forbedre mobil */}
      <div className="flex flex-col md:flex-row gap-6 mb-6">
        {/* Forhåndsvisning - på mobil vises den øverst */}
        <div className="w-full md:hidden mb-2">
          <OrderPreview
            order={order}
            currentStep={currentStep}
            steps={steps}
            requirementFlow={requirementFlow}
            onStepChange={handleStepChange}
            hideDates={isTemplateMode}
          />
        </div>

        {/* Venstre side på desktop - Hovedinnhold */}
        <div className="w-full md:w-2/3">
          {/* Desktop navigasjonsknapper */}
          <div className="hidden md:block">{NavigationButtons}</div>

          {/* Skjema for det aktuelle steget */}
          <div className={transition}>{renderCurrentStep()}</div>
        </div>

        {/* Høyre side på desktop - Forhåndsvisning */}
        <div className="w-full md:w-1/3 sticky top-4 self-start hidden md:block">
          <OrderPreview
            order={order}
            currentStep={currentStep}
            steps={steps}
            requirementFlow={requirementFlow}
            onStepChange={handleStepChange}
            hideDates={isTemplateMode}
          />
        </div>
      </div>

      {/* Sticky navigasjonsknapper nederst på mobil */}
      <div
        className={cn(
          "fixed bottom-0 left-0 right-0 p-4 shadow-lg border-t border-border md:hidden z-10 flex justify-between items-center",
          layoutPage,
        )}
      >
        {prevStep !== null ? (
          <Button
            variant="secondary"
            onClick={() => handleStepChange(prevStep)}
            icon={<HiOutlineArrowLeft />}
            className="flex-1 mr-2"
          >
            {t("orders:create.back_button")}
          </Button>
        ) : (
          <div className="flex-1 mr-2"></div>
        )}
        {nextStep !== null ? (
          <Button
            variant="primary"
            onClick={() => handleStepChange(nextStep)}
            icon={<HiOutlineArrowRight />}
            iconPosition="right"
            className="bg-brand hover:bg-lgb-blue-400 text-white flex-1"
          >
            {t("orders:create.proceed_button")}
          </Button>
        ) : isTemplateMode ? (
          <Button
            variant="primary"
            onClick={() => setShowSaveTemplateDialog(true)}
            icon={<HiCheck />}
            iconPosition="right"
            className="bg-brand hover:bg-lgb-blue-400 text-white flex-1"
          >
            {t("templates.save_template_button")}
          </Button>
        ) : !isEdit ? (
          <Button
            variant="primary"
            onClick={() => {
              if (
                requirementFlow &&
                vehicleRequirements.some((r) => r.value === "")
              ) {
                setValidationMessage(t("workspace:features.missingValue"));
                setShowValidationToast(true);
                return;
              }
              setShowSummaryDialog(true);
            }}
            icon={<HiCheck />}
            iconPosition="right"
            className="bg-brand hover:bg-lgb-blue-400 text-white flex-1"
          >
            {t("create.summary_and_complete_button")}
          </Button>
        ) : (
          <Button
            variant="primary"
            disabled={false}
            onClick={handleSaveOrder}
            icon={<HiCheck />}
            iconPosition="right"
            className="bg-brand hover:bg-lgb-blue-400 text-white flex-1"
          >
            {t("common:save")}
          </Button>
        )}
      </div>

      {/* Legg til ekstra padding i bunnen på mobil for å unngå at innhold skjules bak sticky knapperad */}
      <div className="md:hidden h-20"></div>

      {!isTemplateMode && !isEdit && SummaryAndCompleteDialog}
      {isTemplateMode && (
        <OrderTemplateSaveDialog
          show={showSaveTemplateDialog}
          isLoading={savingTemplate}
          initialName={initialTemplate?.name}
          initialDescription={initialTemplate?.description}
          initialRecurrence={initialTemplate?.recurrence}
          initialProjectNumber={order?.projectNumber}
          onConfirm={handleSaveTemplate}
          onCancel={() => setShowSaveTemplateDialog(false)}
        />
      )}
      {CancelDialog}
    </div>
  );
};

export default CreateOrder;
